import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { rootReducer } from '@wellesley/store'

export type UploadProgressEntry = {
  error: string | null
  finished: boolean
  progress: number
}

export type UploadsProgressState = Record<string, UploadProgressEntry>

const initialState: UploadsProgressState = {}
const defaultEntry: UploadProgressEntry = {
  progress: 0,
  error: null,
  finished: false,
}

export const uploadsProgressSlice = createSlice({
  name: 'uploadsProgress',
  initialState,
  reducers: {
    setProgress: (
      state,
      action: PayloadAction<{ id: string; percent: number }>,
    ) => {
      const { id, percent } = action.payload
      const prev = state[id]
      state[id] = {
        progress: percent,
        error: prev?.error ?? null,
        finished: prev?.finished ?? false,
      }
    },
    setFinished: (
      state,
      action: PayloadAction<{ id: string; finished?: boolean }>,
    ) => {
      const { id, finished = true } = action.payload
      const prev = state[id]
      state[id] = {
        progress: prev?.progress ?? 0,
        error: prev?.error ?? null,
        finished,
      }
    },
    setError: (
      state,
      action: PayloadAction<{ id: string; error?: string }>,
    ) => {
      const { id, error = null } = action.payload
      const prev = state[id]
      state[id] = {
        progress: prev?.progress ?? 0,
        error,
        finished: prev?.finished ?? false,
      }
    },
    resetProgress: (state, action: PayloadAction<string | undefined>) => {
      const id = action.payload
      if (id) delete state[id]
      else {
        Object.keys(state).forEach((k) => delete state[k])
      }
    },
  },
  selectors: {
    selectHasError: (state: UploadsProgressState) =>
      Object.values(state).some((v) => !!v.error),
    selectStateById: (state: UploadsProgressState, id: string) =>
      state[id] ?? defaultEntry,
    selectProgressById: (state: UploadsProgressState, id: string) =>
      state[id]?.progress ?? 0,
    selectErrorById: (state: UploadsProgressState, id: string) =>
      state[id]?.error ?? null,
    selectFinishedById: (state: UploadsProgressState, id: string) =>
      state[id]?.finished ?? false,
  },
}).injectInto(rootReducer)
